import { useState, useCallback, useRef } from 'react';
import { roaryApi } from '../api/modules/roary';

// ─── Types ─────────────────────────────────────────────────────

export interface AuditPageResult {
  pageId: string;
  title: string;
  slug: string;
  url: string;
  score: number;
  issues: Array<{
    type: 'error' | 'warning' | 'info';
    message: string;
    recommendation?: string;
  }>;
  imageIssues: Array<{
    componentId: string;
    componentType: string;
    componentLabel: string;
    itemLabel?: string;
    imageUrl: string;
    problem: string;
  }>;
}

export interface AuditFixItem {
  id: string;
  pageId: string;
  pageTitle: string;
  description: string;
  tool: string;
  priority: 'high' | 'medium' | 'low';
}

export interface AuditManualItem {
  pageId: string;
  pageTitle: string;
  description: string;
  recommendation: string;
}

export interface AuditReport {
  auditId: string;
  siteId: string;
  siteName: string;
  overallScore: number;
  pages: AuditPageResult[];
  summary: string;
  fixableCount: number;
  manualCount: number;
  fixes: AuditFixItem[];
  manualActions: AuditManualItem[];
}

export interface FixResult {
  auditId: string;
  fixedCount: number;
  failedCount: number;
  beforeScore: number;
  afterScore: number;
  remainingIssues: AuditManualItem[];
}

export interface FixProgressItem {
  toolName: string;
  description: string;
  status: 'in_progress' | 'completed' | 'failed';
  summary?: string;
}

export type AuditStatus = 'idle' | 'auditing' | 'report' | 'fixing' | 'complete' | 'error';

// ─── Hook ──────────────────────────────────────────────────────

export function useRoarySEOAudit() {
  const [status, setStatus] = useState<AuditStatus>('idle');
  const [auditReport, setAuditReport] = useState<AuditReport | null>(null);
  const [fixResult, setFixResult] = useState<FixResult | null>(null);
  const [fixProgress, setFixProgress] = useState<FixProgressItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [analyzedPages, setAnalyzedPages] = useState<Array<{ pageTitle: string; score: number }>>(
    []
  );

  const cancelRef = useRef<(() => void) | null>(null);

  const startAudit = useCallback((siteId: string) => {
    setStatus('auditing');
    setError(null);
    setAuditReport(null);
    setFixResult(null);
    setFixProgress([]);
    setAnalyzedPages([]);

    const cancel = roaryApi.startSEOAudit(
      siteId,
      (event, data) => {
        const d = data as Record<string, unknown>;

        switch (event) {
          case 'page_analyzed':
            setAnalyzedPages(prev => [
              ...prev,
              { pageTitle: d.pageTitle as string, score: d.score as number },
            ]);
            break;

          case 'audit_report':
            setAuditReport(d as unknown as AuditReport);
            setStatus('report');
            break;

          case 'marshmallow_update':
            // Could forward to useMarshmallows — for now, just log
            break;

          case 'error':
            setError(d.message as string);
            setStatus('error');
            break;
        }
      },
      err => {
        setError(err.message);
        setStatus('error');
      },
      () => {
        // SSE complete — status already set by audit_report event
      }
    );

    cancelRef.current = cancel;
  }, []);

  const startFix = useCallback((auditId: string) => {
    setStatus('fixing');
    setError(null);
    setFixProgress([]);

    const cancel = roaryApi.startSEOFix(
      auditId,
      (event, data) => {
        const d = data as Record<string, unknown>;

        switch (event) {
          case 'tool_start':
            setFixProgress(prev => [
              ...prev,
              {
                toolName: d.toolName as string,
                description: d.description as string,
                status: 'in_progress',
              },
            ]);
            break;

          case 'tool_end':
            setFixProgress(prev => {
              const updated = [...prev];
              let last = -1;
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].status === 'in_progress') {
                  last = i;
                  break;
                }
              }
              if (last >= 0) {
                updated[last] = {
                  ...updated[last],
                  status: d.success ? 'completed' : 'failed',
                  summary: d.summary as string,
                };
              }
              return updated;
            });
            break;

          case 'fix_complete':
            setFixResult(d as unknown as FixResult);
            setStatus('complete');
            break;

          case 'marshmallow_update':
            break;

          case 'error':
            setError(d.message as string);
            setStatus('error');
            break;
        }
      },
      err => {
        setError(err.message);
        setStatus('error');
      },
      () => {
        // SSE complete
      }
    );

    cancelRef.current = cancel;
  }, []);

  const cancel = useCallback(() => {
    cancelRef.current?.();
    cancelRef.current = null;
    if (status === 'auditing') {
      setStatus('idle');
    } else if (status === 'fixing') {
      setStatus('report'); // Go back to report view
    }
  }, [status]);

  const reset = useCallback(() => {
    cancelRef.current?.();
    cancelRef.current = null;
    setStatus('idle');
    setAuditReport(null);
    setFixResult(null);
    setFixProgress([]);
    setError(null);
    setAnalyzedPages([]);
  }, []);

  return {
    status,
    auditReport,
    fixResult,
    fixProgress,
    error,
    analyzedPages,
    startAudit,
    startFix,
    cancel,
    reset,
  };
}
